import * as riskActions from './actions';

import {
	GetSpatialRecordResponse,
	SpatialLayer,
	SpatialMetaData,
	getRiskScore,
	getSpatialLayers,
	getSpatialMetadata,
	getSpatialRecord,
} from '../../services/risk';
import { put, retry, select, takeEvery } from 'redux-saga/effects';
import {
	selectRiskScoreByAssetId,
	selectSpatialRecordById,
} from './selectors';

import { AssetRiskAndCondition } from '../../features/AssetGrid/types/risk.types';
import { AxiosResponse } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';

function* getSpatialLayersSaga() {
	const { getSpatialLayersResolved, getSpatialLayersRejected } = riskActions;
	try {
		const payload: AxiosResponse<SpatialLayer[]> = yield retry(
			5,
			1500,
			getSpatialLayers,
		);
		if (payload.data) {
			yield put(getSpatialLayersResolved(payload.data));
		} else {
			yield put(getSpatialLayersRejected());
		}
	} catch (e) {
		yield put(getSpatialLayersRejected());
	}
}

function* getSpatialRecordSaga(action: PayloadAction<string>) {
	// check if record has already been retrieved before requesting it
	const record: ReturnType<typeof selectSpatialRecordById> = yield select(
		state => selectSpatialRecordById(state, action.payload),
	);
	if (record) return;
	const { getSpatialRecordResolved, getSpatialRecordRejected } = riskActions;
	try {
		const payload: AxiosResponse<GetSpatialRecordResponse> = yield retry(
			5,
			1500,
			getSpatialRecord,
			action.payload,
		);
		if (payload.data.record) {
			yield put(getSpatialRecordResolved(payload.data.record));
		} else {
			yield put(getSpatialRecordRejected());
		}
	} catch (e) {
		yield put(getSpatialRecordRejected());
	}
}

function* getRiskScoreSaga(action: PayloadAction<string>) {
	// check if risk score has already been retrieved before requesting it
	const riskScore: ReturnType<typeof selectRiskScoreByAssetId> = yield select(
		state => selectRiskScoreByAssetId(state, action.payload),
	);
	if (riskScore) return;
	const { getRiskScoreResolved, getRiskScoreRejected } = riskActions;
	try {
		const payload: AxiosResponse<AssetRiskAndCondition[]> = yield retry(
			5,
			1500,
			getRiskScore,
			action.payload,
		);
		if (payload.data) {
			yield put(
				getRiskScoreResolved({
					assetId: action.payload,
					risk: payload.data[0],
				}),
			);
		} else {
			yield put(getRiskScoreRejected());
		}
	} catch (e) {
		yield put(getRiskScoreRejected());
	}
}

function* getSpatialMetadataSaga() {
	const {
		getSpatialMetadataResolved,
		getSpatialMetadataRejected,
	} = riskActions;
	try {
		const payload: AxiosResponse<SpatialMetaData> = yield retry(
			5,
			1500,
			getSpatialMetadata,
		);
		if (payload.data) {
			yield put(getSpatialMetadataResolved(payload.data));
		} else {
			yield put(getSpatialMetadataRejected());
		}
	} catch (e) {
		yield put(getSpatialMetadataRejected());
	}
}

function* watchGetSpatialLayers() {
	yield takeEvery(riskActions.getSpatialLayers, getSpatialLayersSaga);
}

function* watchGetSpatialRecord() {
	yield takeEvery(riskActions.getSpatialRecord, getSpatialRecordSaga);
}

function* watchGetRiskScore() {
	yield takeEvery(riskActions.getRiskScore, getRiskScoreSaga);
}

function* watchGetSpatialMetadata() {
	yield takeEvery(riskActions.getSpatialMetadata, getSpatialMetadataSaga);
}

export default [
	watchGetSpatialLayers(),
	watchGetSpatialRecord(),
	watchGetRiskScore(),
	watchGetSpatialMetadata(),
];
